import { Form, Select } from "antd";
import { SelectProps } from "antd/lib";
import { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import { useWatch } from "rc-field-form";
import React, { useEffect, useState } from "react";
import { apiObjUrl } from "src/common/apiUrl";
import { makeDictionary } from "src/common/makeDictionary";
import { onError } from "src/common/onError";
import { restWithObjectVersion } from "src/common/restWithObjectVersion";
import { ZAttribute } from "src/types/ZAttribute";
import { ZObjectItem, zObjectItem } from "src/types/ZObjectItem";
import { ZUid } from "src/types/zUid";

const createRoleAttsLoader = (roleId: ZUid) => async () => {
  try {
    const resp = await restWithObjectVersion.get(apiObjUrl(`/roles/${roleId}`));
    return zObjectItem.parse(resp.data).attributes;
  } catch (error) {
    onError(error);
    return [];
  }
};

type PropsSelectAttributesRoles = Omit<SelectProps, "options"> & {
  loader: () => Promise<ZAttribute[]>;
};

const SelectAttributesRoles: React.FC<PropsSelectAttributesRoles> = observer(
  ({ loader, ...props }) => {
    const [atts, setAtts] = useState<ZAttribute[]>([]);
    const options = atts.map((a) => ({ label: a.name, value: a.id }));
    useEffect(() => {
      loader().then(setAtts).catch(onError);
    }, []);
    return <Select {...props} options={options} />;
  },
);

const store = makeAutoObservable({
  rolesMap: null as Record<ZUid, ZObjectItem> | null,
  setRolesMap(map: Record<ZUid, ZObjectItem>) {
    this.rolesMap = map;
  },
  async init() {
    try {
      const resp = await restWithObjectVersion.get(apiObjUrl("/roles"));
      const roles = zObjectItem.array().parse(resp.data);
      this.setRolesMap(makeDictionary(roles, ({ roleId }) => roleId ?? ""));
    } catch (error) {
      onError(error);
    }
  },
  getRoleId(key: ZUid) {
    return this.rolesMap?.[key] ? this.rolesMap[key]?.id : undefined;
  },
  getRoleLabel(key: ZUid) {
    return this.rolesMap?.[key] ? this.rolesMap[key]?.name : undefined;
  },
});

type PropsSelectAttributesRolesList = { rolesIds: ZUid[]; name: string[] };

export const SelectAttributesRolesList: React.FC<PropsSelectAttributesRolesList> =
  observer(({ rolesIds, name }) => {
    const form = Form.useFormInstance();
    const formData = useWatch(name, form);
    useEffect(() => {
      store.init();
    }, []);

    const setRolesData = (roleId: ZUid, attsIds: ZUid[]) => {
      const newData: Record<ZUid, ZUid[]> = {};
      rolesIds.forEach((id) => {
        const key = store.getRoleId(id);
        const piece = key ? formData[key] : undefined;
        if (key && piece) newData[key] = piece;
      });
      form.setFieldValue(name, { ...newData, [roleId]: attsIds });
    };

    if (!store.rolesMap) return null;

    return rolesIds.map((id) => {
      const roleId = store.getRoleId(id);
      const roleLabel = store.getRoleLabel(id);
      if (!roleId) return null;
      return (
        <Form.Item
          label={t('Attributes to display the role "{{role}}"', {
            role: roleLabel,
          })}
          key={id}
          rules={[
            {
              required: true,
              message: t("The field is mandatory"),
            },
          ]}
          name={[...name, roleId]}
        >
          <SelectAttributesRoles
            mode="multiple"
            loader={createRoleAttsLoader(roleId)}
            onChange={(value) => {
              setRolesData(roleId, value as string[]);
            }}
          />
        </Form.Item>
      );
    });
  });
